// src/pages/AdminUsers.js
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TableSortLabel,
  Toolbar,
  Button,
  MenuItem,
  TablePagination,
} from '@mui/material'

import { fetchWithAuth } from '../utils/fetchWithAuth'
import EditButton from '../components/EditButtonModal'

export default function AdminUsers() {
  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)

  const [editOpen, setEditOpen] = useState(false)
  const [editing, setEditing] = useState(null)

  const [orderBy, setOrderBy] = useState('id')
  const [order, setOrder] = useState('asc')

  const [filterText, setFilterText] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const fetchUsers = async () => {
    try {
      const res = await fetchWithAuth('/admin/users')

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))

        console.error(
          'Chyba pri načítaní používateľov:',
          err.message || res.status
        )

        setUsers([])
        return
      }

      const data = await res.json()

      setUsers(
        Array.isArray(data)
          ? data
          : []
      )
    } catch (error) {
      console.error(
        'Chyba pri načítaní používateľov:',
        error
      )
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  function countByLevel(children = []) {
    const counts = {}

    children.forEach(c => {
      counts[c.level] =
        (counts[c.level] || 0) + 1
    })

    return Object.entries(counts)
      .map(([lvl, cnt]) => `L${lvl}:${cnt}`)
      .join(', ')
  }

  const columns = useMemo(
    () => [
      {
        id: 'id',
        label: 'ID',
        get: u => u.id
      },
      {
        id: 'meno',
        label: 'Meno',
        get: u => u.meno || ''
      },
      {
        id: 'priezvisko',
        label: 'Priezvisko',
        get: u => u.priezvisko || ''
      },
      {
        id: 'email',
        label: 'Email',
        get: u => u.email || ''
      },
      {
        id: 'telefon',
        label: 'Telefón',
        get: u => u.telefon || ''
      },
      {
        id: 'referral_code',
        label: 'Referral',
        get: u => u.referral_code || ''
      },
      {
        id: 'parent_referral_code',
        label: 'Parent Referral',
        get: u => u.parent_referral_code || '-'
      },
      {
        id: 'clenstvo_id',
        label: 'Členstvo ID',
        get: u => u.clenstvo_id ?? '-'
      },
      {
        id: 'membership_type',
        label: 'Členstvo typ',
        get: u =>
          u.membership?.membershipType?.type || '-'
      },
      {
        id: 'valid_range',
        label: 'Platnosť od – do',
        get: u =>
          u.membership
            ? `${u.membership.valid_from?.split('T')[0]} – ${u.membership.valid_to?.split('T')[0]}`
            : '-'
      },
      {
        id: 'network_levels',
        label: 'Siete (úrovne)',
        get: u =>
          countByLevel(u.networkChildren)
      },
      {
        id: 'stav_uctu',
        label: 'Registrácia potvrdená',
        get: u => u.stav_uctu
      },
      {
        id: 'aktivny',
        label: 'Aktívne členstvo',
        get: u =>
          u.aktivny
            ? 'Áno'
            : 'Nie'
      },
      {
        id: 'ulica',
        label: 'Ulica',
        get: u => u.ulica || ''
      },
      {
        id: 'mesto',
        label: 'Mesto',
        get: u => u.mesto || ''
      },
      {
        id: 'psc',
        label: 'PSČ',
        get: u => u.psc || ''
      },
      {
        id: 'krajina',
        label: 'Krajina',
        get: u => u.krajina || ''
      },
      {
        id: 'banka',
        label: 'Banka',
        get: u => u.banka || ''
      },
      {
        id: 'iban',
        label: 'IBAN',
        get: u => u.iban || ''
      },
      {
        id: 'swift',
        label: 'SWIFT',
        get: u => u.swift || ''
      },
      {
        id: 'vop_suhlas',
        label: 'VOP súhlas',
        get: u =>
          u.vop_suhlas
            ? 'Áno'
            : 'Nie'
      },
      {
        id: 'createdAt',
        label: 'Vytvorené',
        get: u =>
          u.createdAt?.split('T')[0] || ''
      },
      {
        id: 'updatedAt',
        label: 'Upravené',
        get: u =>
          u.updatedAt?.split('T')[0] || ''
      },
    ],
    []
  )

  const processedUsers = useMemo(() => {
    const q =
      filterText
        .trim()
        .toLowerCase()

    const filtered = q
      ? users.filter(u => {
          const values = [
            String(u.id ?? ''),
            u.meno,
            u.priezvisko,
            u.email,
            u.telefon,
            u.referral_code,
            u.parent_referral_code,
            u.clenstvo_id,
            u.membership?.membershipType?.type,
            u.membership?.valid_from,
            u.membership?.valid_to,
            countByLevel(u.networkChildren),
            u.stav_uctu,
            u.aktivny
              ? 'Áno'
              : 'Nie',
            u.ulica,
            u.mesto,
            u.psc,
            u.krajina,
            u.banka,
            u.iban,
            u.swift,
            u.vop_suhlas
              ? 'Áno'
              : 'Nie',
            u.createdAt,
            u.updatedAt,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()

          return values.includes(q)
        })
      : users

    const col =
      columns.find(c => c.id === orderBy) ||
      columns[0]

    const withValue =
      filtered.map(u => ({
        u,
        v: col.get(u)
      }))

    withValue.sort((a, b) => {
      const av = a.v ?? ''
      const bv = b.v ?? ''

      const an =
        typeof av === 'number'
          ? av
          : Number.isFinite(+av) && av !== ''
            ? +av
            : null

      const bn =
        typeof bv === 'number'
          ? bv
          : Number.isFinite(+bv) && bv !== ''
            ? +bv
            : null

      let cmp = 0

      if (
        an !== null &&
        bn !== null
      ) {
        cmp = an - bn
      } else {
        cmp = String(av).localeCompare(
          String(bv),
          'sk',
          {
            sensitivity: 'base',
            numeric: true
          }
        )
      }

      return order === 'asc'
        ? cmp
        : -cmp
    })

    return withValue.map(x => x.u)
  }, [
    users,
    filterText,
    order,
    orderBy,
    columns
  ])

  const paginatedUsers = processedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  const handleSort = id => {
    if (orderBy === id) {
      setOrder(prev =>
        prev === 'asc'
          ? 'desc'
          : 'asc'
      )
    } else {
      setOrderBy(id)
      setOrder('asc')
    }
  }

  const openEdit = user => {
    setEditing({
      id: user.id,
      meno: user.meno || '',
      priezvisko: user.priezvisko || '',
      email: user.email || '',
      telefon: user.telefon || '',
      referral_code: user.referral_code || '',
      parent_referral_code: user.parent_referral_code || '',
      clenstvo_id: user.clenstvo_id ?? '',
      stav_uctu: user.stav_uctu || '',
      aktivny: !!user.aktivny,
      ulica: user.ulica || '',
      mesto: user.mesto || '',
      psc: user.psc || '',
      krajina: user.krajina || '',
      banka: user.banka || '',
      iban: user.iban || '',
      swift: user.swift || '',
      vop_suhlas: !!user.vop_suhlas
    })

    setEditOpen(true)
  }

  const closeEdit = () => {
    setEditOpen(false)
    setEditing(null)
  }

  const saveEdit = async () => {
    if (!editing) return

    const res = await fetchWithAuth(`/admin/users/${editing.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...editing,
        clenstvo_id:
          editing.clenstvo_id === ''
            ? null
            : Number(editing.clenstvo_id)
      })
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))

      alert(
        err?.detail ||
        err?.message ||
        'Chyba pri ukladaní'
      )

      return
    }

    setEditOpen(false)
    setEditing(null)

    await fetchUsers()
  }

  const detailLabels = {
    id: 'ID',
    meno: 'Meno',
    priezvisko: 'Priezvisko',
    email: 'Email',
    telefon: 'Telefón',
    referral_code: 'Referral',
    parent_referral_code: 'Parent Referral',
    clenstvo_id: 'Členstvo ID',
    stav_uctu: 'Registrácia potvrdená',
    aktivny: 'Aktívne členstvo',
    ulica: 'Ulica',
    mesto: 'Mesto',
    psc: 'PSČ',
    krajina: 'Krajina',
    banka: 'Banka',
    iban: 'IBAN',
    swift: 'SWIFT',
    vop_suhlas: 'VOP súhlas',
    createdAt: 'Vytvorené',
    updatedAt: 'Upravené',
  }

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #107c6b',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          marginBottom: 1
        }}
      >
        <h3
          style={{
            fontSize: 16,
            margin: 0
          }}
        >
          Zoznam používateľov
        </h3>

        <Typography
          sx={{
            fontSize: 13,
            opacity: 0.7
          }}
        >
          Záznamov: {processedUsers.length}
        </Typography>
      </Box>

      <Toolbar
        disableGutters
        sx={{
          minHeight: '42px !important',
          marginBottom: 1
        }}
      >
        <TextField
          value={filterText}
          onChange={e => {
            setFilterText(e.target.value)
            setPage(0)
          }}
          size="small"
          label="Filtrovať (full-text)"
          placeholder="Hľadať podľa mena, emailu, IBAN, mesta…"
          sx={{
            width: 400,
            maxWidth: '100%',
            '& .MuiInputBase-root': {
              fontSize: 13
            },
            '& .MuiInputLabel-root': {
              fontSize: 13
            }
          }}
        />
      </Toolbar>

      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          border: '1px solid #c7c7c7',
          borderRadius: '10px',
          boxShadow: 'none'
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 1500,

            '& thead th': {
              fontWeight: 'bold',
              backgroundColor: '#c8e1e1',
              whiteSpace: 'nowrap',
              fontSize: 12,
              padding: '6px 8px'
            },

            '& tbody td': {
              fontSize: 12,
              padding: '5px 8px',
              whiteSpace: 'nowrap'
            },

            '& .MuiTableSortLabel-root': {
              fontSize: 12
            }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>
                Upraviť
              </TableCell>

              {columns.map(col => (
                <TableCell
                  key={col.id}
                  sortDirection={
                    orderBy === col.id
                      ? order
                      : false
                  }
                >
                  <TableSortLabel
                    active={
                      orderBy === col.id
                    }
                    direction={
                      orderBy === col.id
                        ? order
                        : 'asc'
                    }
                    onClick={() =>
                      handleSort(col.id)
                    }
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedUsers.map(u => (
              <TableRow
                key={u.id}
                hover
                onClick={() =>
                  setSelectedUser(u)
                }
                sx={{
                  cursor: 'pointer',

                  '&:hover': {
                    backgroundColor:
                      '#f5f5f5'
                  }
                }}
              >
                <TableCell>
                  <EditButton
                    onClick={e => {
                      e.stopPropagation()
                      openEdit(u)
                    }}
                  />
                </TableCell>

                {columns.map(col => (
                  <TableCell key={col.id}>
                    {(() => {
                      const val = col.get(u)

                      return typeof val === 'object'
                        ? JSON.stringify(val)
                        : String(val)
                    })()}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <TablePagination
          component="div"
          count={processedUsers.length}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Na stránku"
        />
      </Box>

      <Dialog
        open={!!selectedUser}
        onClose={() =>
          setSelectedUser(null)
        }
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detail používateľa
        </DialogTitle>

        <DialogContent dividers>
          {selectedUser && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                marginTop: 1
              }}
            >
              {Object.entries(
                selectedUser
              ).map(([key, val]) => (
                <Typography
                  key={key}
                  sx={{
                    fontSize: 13
                  }}
                >
                  <strong>
                    {detailLabels[key] || key}:
                  </strong>{' '}

                  {typeof val === 'object'
                    ? JSON.stringify(val)
                    : String(val)}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={closeEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upraviť používateľa
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <TextField
            label="ID"
            value={editing?.id ?? ''}
            disabled
            size="small"
          />

          <TextField
            label="Meno"
            value={editing?.meno ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                meno: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            label="Priezvisko"
            value={editing?.priezvisko ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                priezvisko: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            label="Email"
            value={editing?.email ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                email: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            label="Telefón"
            value={editing?.telefon ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                telefon: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            label="Referral"
            value={editing?.referral_code ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                referral_code: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            label="Parent Referral"
            value={editing?.parent_referral_code ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                parent_referral_code: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            label="Členstvo ID"
            type="number"
            value={editing?.clenstvo_id ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                clenstvo_id: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            select
            label="Registrácia potvrdená"
            value={editing?.stav_uctu ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                stav_uctu: e.target.value
              }))
            }
            size="small"
          >
            <MenuItem value="pending">
              pending
            </MenuItem>

            <MenuItem value="active">
              active
            </MenuItem>

            <MenuItem value="deactivated">
              deactivated
            </MenuItem>
          </TextField>

          <TextField
            select
            label="Aktívne členstvo"
            value={
              editing?.aktivny
                ? 'true'
                : 'false'
            }
            onChange={e =>
              setEditing(v => ({
                ...v,
                aktivny:
                  e.target.value === 'true'
              }))
            }
            size="small"
          >
            <MenuItem value="true">
              Áno
            </MenuItem>

            <MenuItem value="false">
              Nie
            </MenuItem>
          </TextField>

          <TextField
            label="Ulica"
            value={editing?.ulica ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                ulica: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            label="Mesto"
            value={editing?.mesto ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                mesto: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            label="PSČ"
            value={editing?.psc ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                psc: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            label="Krajina"
            value={editing?.krajina ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                krajina: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            label="Banka"
            value={editing?.banka ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                banka: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            label="IBAN"
            value={editing?.iban ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                iban: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            label="SWIFT"
            value={editing?.swift ?? ''}
            onChange={e =>
              setEditing(v => ({
                ...v,
                swift: e.target.value
              }))
            }
            size="small"
          />

          <TextField
            select
            label="VOP súhlas"
            value={
              editing?.vop_suhlas
                ? 'true'
                : 'false'
            }
            onChange={e =>
              setEditing(v => ({
                ...v,
                vop_suhlas:
                  e.target.value === 'true'
              }))
            }
            size="small"
          >
            <MenuItem value="true">
              Áno
            </MenuItem>

            <MenuItem value="false">
              Nie
            </MenuItem>
          </TextField>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeEdit}
          >
            Zrušiť
          </Button>

          <Button
            variant="contained"
            onClick={saveEdit}
          >
            Uložiť
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}